import { useState } from "react";
import axios from "axios";
import { PageHero, useTitle } from "../components/PageHero";
import { Tag } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  useTitle("Admin — RFQ & Enquiries | SABAKT");
  const [key, setKey] = useState("");
  const [tab, setTab] = useState("rfqs");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async (which = tab) => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/${which}`, { headers: { "X-Admin-Key": key } });
      setRows(res.data);
      setTab(which);
    } catch (e) {
      setRows([]);
      setError(e.response?.status === 401 ? "Invalid admin key." : "Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <PageHero eyebrow="Internal" testid="admin-hero" title="RFQ & Enquiry Inbox."
        text="Enter the admin key to view submissions. The key is never stored in the browser." />
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-8">
          <form onSubmit={(e) => { e.preventDefault(); load(tab); }} className="flex flex-wrap items-end gap-4 border border-seam bg-iron p-6" data-testid="admin-key-form">
            <div className="min-w-64 flex-1">
              <span className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-cadmium">Admin Key</span>
              <input type="password" value={key} onChange={(e) => setKey(e.target.value)} required autoComplete="off"
                className="w-full border border-seam bg-obsidian px-4 py-3 text-sm text-frost focus:border-spark focus:outline-none"
                data-testid="admin-key-input" />
            </div>
            <button type="submit" disabled={loading} data-testid="admin-load-btn"
              className="bg-spark px-6 py-3 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-gold disabled:opacity-50">
              {loading ? "Loading..." : "Load Submissions"}
            </button>
          </form>

          {error && <p className="mt-6 border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm text-red-400" data-testid="admin-error">{error}</p>}

          {rows.length > 0 && (
            <div className="mt-8" data-testid="admin-results">
              <div className="flex gap-px border border-seam bg-seam">
                {["rfqs", "enquiries"].map((t) => (
                  <button key={t} onClick={() => load(t)} data-testid={`admin-tab-${t}`}
                    className={`px-6 py-3 font-display text-sm font-bold uppercase tracking-wider ${tab === t ? "bg-spark text-white" : "bg-iron text-cadmium hover:text-frost"}`}>
                    {t === "rfqs" ? "RFQs" : "Enquiries"}
                  </button>
                ))}
              </div>
              <div className="mt-4 overflow-x-auto border border-seam">
                <table className="w-full min-w-[900px] text-left text-sm">
                  <thead className="bg-iron font-mono text-[10px] uppercase tracking-widest text-cadmium">
                    <tr>
                      {(tab === "rfqs"
                        ? ["Date", "Ref", "Name", "Company", "Email", "Mobile", "Type", "Files", "Description"]
                        : ["Date", "Name", "Company", "Email", "Phone", "Reason", "Message"]
                      ).map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-seam/60">
                    {rows.map((r) => (
                      <tr key={r.id} className="bg-obsidian align-top text-frost/85" data-testid={`admin-row-${r.ref || r.id}`}>
                        <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-cadmium">{new Date(r.created_at).toLocaleString()}</td>
                        {tab === "rfqs" ? (
                          <>
                            <td className="px-4 py-3 font-mono text-xs text-spark">{r.ref}</td>
                            <td className="px-4 py-3">{r.name}</td>
                            <td className="px-4 py-3">{r.company || "—"}</td>
                            <td className="px-4 py-3">{r.email}</td>
                            <td className="px-4 py-3">{r.mobile}</td>
                            <td className="px-4 py-3">{r.requirement_type || "—"}</td>
                            <td className="px-4 py-3 text-xs text-cadmium">{r.files?.map((f) => f.original).join(", ") || "—"}</td>
                            <td className="max-w-72 px-4 py-3 text-xs text-cadmium">{r.description}</td>
                          </>
                        ) : (
                          <>
                            <td className="px-4 py-3">{r.name}</td>
                            <td className="px-4 py-3">{r.company || "—"}</td>
                            <td className="px-4 py-3">{r.email}</td>
                            <td className="px-4 py-3">{r.phone || "—"}</td>
                            <td className="px-4 py-3">{r.reason}</td>
                            <td className="max-w-96 px-4 py-3 text-xs text-cadmium">{r.message}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-cadmium/50">
                <Tag>{rows.length} record(s)</Tag>
              </p>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}
